import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, ChevronUp, ChevronDown, Eye, Save,
  ImagePlus, GripVertical, Loader2, AlertCircle, CheckCircle, Sparkles,
  Copy, Users, Send, EyeOff, FileDown, Link2,
} from 'lucide-react';
import Shell from '../components/TopBarShell';
import MarkdownTextarea from '../components/MarkdownTextarea';
import NewsletterAdminNav from '../components/NewsletterAdminNav';
import { LANG_LABEL, LangTabs, normalizeTranslations, tabStatusFor } from '../components/LangTabs';
import { LT } from '../lib/appCatalogue';
import { useLocale, useT } from '../contexts/LocaleProvider';
import api from '../utils/api';
import { SectionRenderer, mediaKindFromUrl } from './Newsletters';

/* MANEVA · in-app newsletter CMS. HR (Virginie) drafts and publishes
 * episodes here without touching code. The renderer side stays in
 * Newsletters.jsx; this file is purely the editor surface.
 *
 *   /admin/newsletters      → list of episodes (drafts + published)
 *   /admin/newsletters/<id> → single-episode editor (header + sections)
 *
 * Access is gated by /api/announcements/me/is-hr/ — only members of
 * the HR group can write. Non-HR users see a friendly "not for you"
 * card.
 */

export default function AdminNewsletters() {
  const { id } = useParams();
  const [allowed, setAllowed] = useState(undefined);  // undefined=loading
  useEffect(() => {
    api.canEditContent().then(setAllowed).catch(() => setAllowed(false));
  }, []);

  if (allowed === undefined) return <Shell><LoadingBar /></Shell>;
  if (!allowed) return <Shell><NotAllowed /></Shell>;
  if (id) return <NewsletterEditor id={parseInt(id, 10)} />;
  return <NewslettersList />;
}

const NotAllowed = () => {
  const t = useT();
  return (
    <div className="max-w-[520px] mt-10 rounded-2xl p-6"
         style={{ background: LT.beigeLight, border: `1px dashed ${LT.green30}`, color: LT.green50 }}>
      {t('nl.notAllowed')}
    </div>
  );
};

const LoadingBar = () => {
  const t = useT();
  return (
    <div className="flex items-center gap-2 mt-10 text-[13px]" style={{ color: LT.green50 }}>
      <Loader2 className="h-4 w-4 animate-spin" /> {t('common.loading')}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
 * List page · all episodes (drafts + published), with a "Nouveau"
 * button that pops up an inline form for the slug + number + title.
 * ──────────────────────────────────────────────────────────────── */
const NewslettersList = () => {
  const t = useT();
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [audience, setAudience] = useState(null);  // denominator for « vu par X / Y »
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ number: '', title: '', date_label: '' });
  const [err, setErr] = useState('');

  const load = () => api.adminNewsletters()
    .then(rows => setItems(Array.isArray(rows) ? rows : (rows?.results || [])));
  useEffect(() => {
    load();
    api.adminNewsletterAudienceSize().then(r => setAudience(r?.count ?? null)).catch(() => null);
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      const created = await api.adminCreateNewsletter({
        number: parseInt(form.number, 10) || 0, title: form.title,
        date_label: form.date_label, status: 'draft', locale: 'fr',
      });
      nav(`/admin/newsletters/${created.id}`);
    } catch (e2) {
      const data = e2?.response?.data;
      const first = data && typeof data === 'object' ? Object.values(data).flat()[0] : null;
      setErr(first || e2?.message || t('nl.createFailed'));
    } finally { setBusy(false); }
  };

  return (
    <Shell>
      <NewsletterAdminNav />
      <header className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-[2rem] sm:text-[2.4rem] leading-tight"
              style={{ color: LT.greenDeep }}>
            {t('nl.list.title')}
          </h1>
          <p className="mt-2 text-[14px]" style={{ color: '#3a4f44' }}>
            {t('nl.list.intro')}{' '}
            <Link to="/resources/newsletters" className="underline" style={{ color: LT.greenDeep }}>
              /resources/newsletters
            </Link>.
          </p>
        </div>
        <button onClick={() => {
                  // next number pre-filled · the slug follows from it
                  const next = (items || []).reduce((m, e) => Math.max(m, Number(e.number) || 0), 0) + 1;
                  setForm(f => ({ ...f, number: f.number || String(next) }));
                  setCreating(v => !v);
                }}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-medium transition"
                style={{ background: LT.greenDeep, color: '#fefefe' }}>
          <Plus className="h-4 w-4" /> {t('nl.list.new')}
        </button>
      </header>

      {creating && (
        <form onSubmit={create}
              className="mb-6 rounded-2xl p-5 grid grid-cols-1 md:grid-cols-4 gap-3 items-end"
              style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}` }}>
          <Field label={t('nl.field.number')} required>
            <input type="number" required value={form.number} onChange={e => setForm({ ...form, number: e.target.value })}
                   className="ed-input" />
          </Field>
          <Field label={t('nl.field.dateLabel')} hint={t('nl.create.addressHint', { slug: previewSlug(form.number, form.date_label) })} required>
            <input required value={form.date_label} placeholder={t('nl.field.dateLabel.placeholder')}
                   onChange={e => setForm({ ...form, date_label: e.target.value })}
                   className="ed-input" />
          </Field>
          <Field label={t('nl.field.title')} required className="md:col-span-2">
            <input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                   className="ed-input" />
          </Field>
          {err && <div className="md:col-span-4 text-[12.5px] flex items-center gap-2"
                       style={{ color: '#b54338' }}>
            <AlertCircle className="h-3.5 w-3.5" /> {err}
          </div>}
          <div className="md:col-span-4 flex gap-2">
            <button type="submit" disabled={busy}
                    className="px-4 py-2 rounded-xl text-[13px] font-medium"
                    style={{ background: LT.greenDeep, color: '#fefefe' }}>
              {busy ? t('nl.create.busy') : t('nl.create.submit')}
            </button>
            <button type="button" onClick={() => setCreating(false)}
                    className="px-4 py-2 rounded-xl text-[13px]"
                    style={{ color: LT.green50 }}>
              {t('common.cancel')}
            </button>
          </div>
        </form>
      )}

      <section>
        {items === null && <LoadingBar />}
        {items && items.length === 0 && (
          <div className="rounded-2xl p-6 text-[14px]"
               style={{ background: LT.beigeLight, border: `1px dashed ${LT.green30}`, color: LT.green50 }}>
            {t('nl.list.empty')}
          </div>
        )}
        <ul className="space-y-2">
          {items?.map(ep => (
            <li key={ep.id}>
              <Link to={`/admin/newsletters/${ep.id}`}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl transition hover:-translate-y-px"
                    style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}` }}>
                <span className="font-num text-[12px] shrink-0 w-12 text-center px-1.5 py-1 rounded-md"
                      style={{ background: LT.yellow, color: LT.greenDeep, fontWeight: 600 }}>
                  {t('nl.list.epShort')}{ep.number}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block font-display text-[1rem] leading-tight truncate"
                        style={{ color: LT.greenDeep }}>
                    {ep.title || t('nl.untitled')}
                  </span>
                  <span className="block text-[11.5px] mt-0.5 font-num" style={{ color: LT.green50 }}>
                    {ep.slug} · {ep.date_label || '—'}
                  </span>
                </span>
                <ViewerBadge unique={ep.unique_viewer_count} audience={audience} />
                <StatusPill status={ep.status} />
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </Shell>
  );
};

/* « vu par 23 / 41 » · unique signed-in readers vs. active employees.
 * Hidden until the audience denominator has loaded (the API call runs
 * in parallel with the list fetch) and on rows with zero reads, so a
 * brand-new episode doesn't shout « 0 / 41 » before anyone could have
 * opened it. */
const ViewerBadge = ({ unique, audience }) => {
  const t = useT();
  if (audience == null || unique == null || unique === 0) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.12em] font-medium px-2 py-1 rounded-md font-num shrink-0"
          style={{ background: '#eef2ef', color: '#3a6f4a' }}
          title={t('nl.viewers.hint')}>
      <Users className="h-3 w-3" /> {unique} / {audience}
    </span>
  );
};

/* Lecteurs panel · per-episode readership list shown at the top of
 * the editor. Collapsed by default to keep the header light · opens
 * to two columns: who read the episode (with read-time + locale) and
 * who hasn't yet. Sortable by name / last-read. Hidden entirely on
 * draft episodes since drafts can't be opened by the audience yet. */
const ReadersPanel = ({ newsletterId, status }) => {
  const t = useT();
  const { intlLocale } = useLocale();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);  // {viewers, non_viewers, audience_total}
  const [sortBy, setSortBy] = useState('recent');  // 'recent' | 'name'

  useEffect(() => {
    if (!open || data) return;
    api.adminNewsletterReaders(newsletterId)
      .then(setData)
      .catch(() => setData({ viewers: [], non_viewers: [], audience_total: 0 }));
  }, [open, newsletterId, data]);

  if (status !== 'published') return null;

  const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString(intlLocale, {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const sortedViewers = (() => {
    if (!data?.viewers) return [];
    const v = [...data.viewers];
    if (sortBy === 'name') v.sort((a, b) => a.full_name.localeCompare(b.full_name));
    else v.sort((a, b) => new Date(b.last_viewed_at) - new Date(a.last_viewed_at));
    return v;
  })();

  return (
    <section className="mb-6 rounded-2xl"
             style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}` }}>
      <button type="button" onClick={() => setOpen(o => !o)}
              className="w-full flex items-center justify-between gap-3 px-5 py-3 text-left">
        <span className="inline-flex items-center gap-2">
          <Users className="h-4 w-4" style={{ color: LT.green50 }} />
          <span className="text-[11px] uppercase tracking-[0.14em] font-medium" style={{ color: LT.green50 }}>
            {t('nl.readers')}
          </span>
          {data && (
            <span className="text-[12.5px] font-num" style={{ color: '#3a4f44' }}>
              · {t('nl.readers.opened', { n: data.viewers.length, total: data.audience_total })}
            </span>
          )}
        </span>
        {open ? <ChevronUp className="h-4 w-4" style={{ color: LT.green50 }} />
              : <ChevronDown className="h-4 w-4" style={{ color: LT.green50 }} />}
      </button>

      {open && (
        <div className="px-5 pb-5">
          {data === null && <LoadingBar />}
          {data && (
            <>
              <div className="flex items-center gap-2 mb-3 text-[12px]" style={{ color: LT.green50 }}>
                <span>{t('nl.readers.sortBy')}</span>
                <button onClick={() => setSortBy('recent')}
                        className="px-2 py-0.5 rounded-md"
                        style={{
                          background: sortBy === 'recent' ? LT.greenDeep : 'transparent',
                          color: sortBy === 'recent' ? '#fefefe' : LT.greenDeep,
                          border: `1px solid ${LT.green30}`,
                        }}>{t('nl.readers.sort.recent')}</button>
                <button onClick={() => setSortBy('name')}
                        className="px-2 py-0.5 rounded-md"
                        style={{
                          background: sortBy === 'name' ? LT.greenDeep : 'transparent',
                          color: sortBy === 'name' ? '#fefefe' : LT.greenDeep,
                          border: `1px solid ${LT.green30}`,
                        }}>{t('nl.readers.sort.name')}</button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Read */}
                <div>
                  <div className="text-[11px] uppercase tracking-[0.14em] font-medium mb-2"
                       style={{ color: '#3a6f4a' }}>
                    {t('nl.readers.read')} · {sortedViewers.length}
                  </div>
                  {sortedViewers.length === 0 && (
                    <div className="text-[12.5px]" style={{ color: LT.green50 }}>
                      {t('nl.readers.nobody')}
                    </div>
                  )}
                  <ul className="space-y-1.5">
                    {sortedViewers.map(v => (
                      <li key={v.email}
                          className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg"
                          style={{ background: '#fff', border: `1px solid ${LT.green30}` }}>
                        <Avatar src={v.photo} name={v.full_name} />
                        <span className="flex-1 min-w-0">
                          <span className="block text-[13px] font-medium truncate" style={{ color: LT.greenDeep }}>
                            {v.full_name}
                          </span>
                          <span className="block text-[11px] font-num truncate" style={{ color: LT.green50 }}>
                            {fmtDate(v.last_viewed_at)}
                            {v.view_count > 1 && ` · ${v.view_count}×`}
                            {v.locales.length > 0 && ` · ${v.locales.join('/')}`}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                {/* Not read */}
                <div>
                  <div className="text-[11px] uppercase tracking-[0.14em] font-medium mb-2"
                       style={{ color: '#9c6515' }}>
                    {t('nl.readers.unread')} · {data.non_viewers.length}
                  </div>
                  {data.non_viewers.length === 0 && (
                    <div className="text-[12.5px]" style={{ color: LT.green50 }}>
                      {t('nl.readers.everyone')}
                    </div>
                  )}
                  <ul className="space-y-1.5">
                    {data.non_viewers.map(v => (
                      <li key={v.email}
                          className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg"
                          style={{ background: '#fff', border: `1px dashed ${LT.green30}` }}>
                        <Avatar src={v.photo} name={v.full_name} />
                        <span className="flex-1 min-w-0">
                          <span className="block text-[13px] truncate" style={{ color: '#3a4f44' }}>
                            {v.full_name}
                          </span>
                          {v.poste && (
                            <span className="block text-[11px] truncate" style={{ color: LT.green50 }}>
                              {v.poste}
                            </span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
};

/* Small circular avatar · falls back to initials in a tinted disc when
 * the employee has no photo uploaded yet. Used by ReadersPanel rows. */
const Avatar = ({ src, name }) => {
  if (src) {
    return <img src={src} alt="" className="h-7 w-7 rounded-full object-cover shrink-0"
                style={{ border: `1px solid ${LT.green30}` }} />;
  }
  const initials = (name || '?').split(/\s+/).slice(0, 2).map(s => s[0] || '').join('').toUpperCase();
  return (
    <span className="h-7 w-7 rounded-full grid place-items-center text-[10.5px] font-num font-medium shrink-0"
          style={{ background: LT.yellow, color: LT.greenDeep }}>
      {initials}
    </span>
  );
};

/* Same recipe as the server's unique_episode_slug · shown as a hint while
 * the form is being filled in, the server has the last word. */
const previewSlug = (number, dateLabel) => {
  const tail = (dateLabel || '').replace(/^\s*\d{1,2}(er)?\s+/, '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return `episode-${parseInt(number, 10) || 0}-${tail || 'mois-annee'}`;
};

/* Confirmation before publishing / unpublishing. The Slack box is ticked
 * by default the first time and locked once the episode was announced. */
const PublishPanel = ({ mode, ep, pending, busy, onCancel, onConfirm }) => {
  const t = useT();
  const { intlLocale } = useLocale();
  const [announce, setAnnounce] = useState(!ep.slack_posted_at);
  const publishing = mode === 'publish';
  const pendingNote = pending === 1
    ? ` ${t('nl.publish.pending1')}`
    : pending > 1 ? ` ${t('nl.publish.pendingN', { n: pending })}` : '';
  return (
    <div className="mb-6 rounded-2xl p-5" style={{ background: LT.beigeLight, border: `1px solid ${LT.greenDeep}` }}>
      <div className="font-display text-[1.1rem]" style={{ color: LT.greenDeep }}>
        {t(publishing ? 'nl.publish.title' : 'nl.unpublish.title', { n: ep.number })}
      </div>
      <p className="text-[13.5px] mt-1" style={{ color: '#3a4f44' }}>
        {t(publishing ? 'nl.publish.body' : 'nl.unpublish.body')}
        {pendingNote}
      </p>
      {publishing && (
        <label className="mt-3 inline-flex items-center gap-2 text-[13px] cursor-pointer" style={{ color: LT.greenDeep }}>
          <input type="checkbox" checked={announce} disabled={!!ep.slack_posted_at}
                 onChange={e => setAnnounce(e.target.checked)} />
          {ep.slack_posted_at
            ? t('nl.publish.slackDone', { date: new Date(ep.slack_posted_at).toLocaleDateString(intlLocale) })
            : t('nl.publish.slack')}
        </label>
      )}
      <div className="mt-4 flex items-center gap-2">
        <button onClick={() => onConfirm(announce)} disabled={busy}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[13px] font-medium disabled:opacity-50"
                style={{ background: LT.greenDeep, color: '#fefefe' }}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (publishing ? <Send className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />)}
          {publishing ? t('nl.publish.now') : t('nl.unpublish')}
        </button>
        <button onClick={onCancel} className="ed-btn-ghost">{t('common.cancel')}</button>
      </div>
    </div>
  );
};

const StatusPill = ({ status }) => {
  const t = useT();
  const isPub = status === 'published';
  return (
    <span className="text-[10.5px] uppercase tracking-[0.14em] font-medium px-2 py-1 rounded-md font-num"
          style={{
            background: isPub ? '#e5efe7' : '#fef3e2',
            color: isPub ? '#3a6f4a' : '#9c6515',
          }}>
      {isPub ? t('nl.status.published') : t('nl.status.draft')}
    </span>
  );
};

const Field = ({ label, hint, children, required, className = '' }) => (
  <label className={`flex flex-col gap-1 ${className}`}>
    <span className="text-[11px] uppercase tracking-[0.12em] font-num font-medium"
          style={{ color: LT.green50 }}>
      {label}{required && <span style={{ color: '#b54338' }}> *</span>}
    </span>
    {children}
    {hint && <span className="text-[11px]" style={{ color: LT.green50 }}>{hint}</span>}
  </label>
);

/* LANGS · LANG_LABEL · LangTabs · normalizeTranslations · tabStatusFor come
 * from components/LangTabs, shared with the events editor. */

/* Header-specific helpers · FR is the canonical so it edits the top-
 * level fields, NL/EN edit the translations slot. This keeps legacy
 * readers reading something even when a translation is missing. */
function headerLocalizedField(draft, lang, field) {
  if (lang === 'fr') return draft[field] || '';
  return ((draft.translations || {})[lang] || {})[field] || '';
}
function setLocalizedField(draft, lang, field, value) {
  if (lang === 'fr') return { ...draft, [field]: value };
  const next = { ...(draft.translations || {}) };
  next[lang] = { ...(next[lang] || {}), [field]: value };
  return { ...draft, translations: next };
}
function headerLocalizedFor(draft) {
  if (!draft) return {};
  return {
    ...(draft.translations || {}),
    fr: {
      title: draft.title || '',
      teaser: draft.teaser || '',
      date_label: draft.date_label || '',
    },
  };
}

/* Same shape for a section · the canonical FR copy lives in
 * `title` / `body_markdown`, NL/EN in `translations.{nl,en}`. */
function sectionLocalizedField(draft, lang, field) {
  if (lang === 'fr') return draft[field] || '';
  return ((draft.translations || {})[lang] || {})[field] || '';
}

/* ─────────────────────────────────────────────────────────────────
 * Single-episode editor · header fields on top, then ordered list
 * of sections each with its own inline form, plus an "Ajouter une
 * section" picker at the bottom.
 * ──────────────────────────────────────────────────────────────── */
const NewsletterEditor = ({ id }) => {
  const t = useT();
  const nav = useNavigate();
  const [ep, setEp] = useState(undefined);
  const [headerDraft, setHeaderDraft] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  // Shared language tab for the whole episode editor · the header
  // translations panel and every section's localized fields all read
  // from this state, so an editor working on the NL pass flips one
  // tab at the top and the rest of the page follows.
  const [lang, setLang] = useState('fr');

  const load = () => api.adminNewsletter(id)
    .then(data => { setEp(data); setHeaderDraft({
      number: data.number,
      status: data.status, locale: data.locale,
      // Canonical FR fields stay at the top of the draft · the
      // serializer still writes them so legacy readers see something.
      title: data.title, teaser: data.teaser || '', date_label: data.date_label || '',
      translations: normalizeTranslations(data.translations, ['title', 'teaser', 'date_label']),
    }); setCoverFile(null); });

  useEffect(() => { load(); }, [id]);  // eslint-disable-line react-hooks/exhaustive-deps

  /* Saved through the sticky bar with everything else · the header had
   * its own button too, which made two save models on one page. */
  const saveHeader = async () => {
    const payload = { ...headerDraft };
    if (coverFile) payload.cover = coverFile;
    const updated = await api.adminUpdateNewsletter(id, payload);
    setEp(prev => ({ ...prev, ...updated }));
    setCoverFile(null);
  };

  const deleteEpisode = async () => {
    if (!confirm(t('nl.deleteEpisodeConfirm'))) return;
    await api.adminDeleteNewsletter(id);
    nav('/admin/newsletters');
  };

  const reload = () => api.adminNewsletter(id).then(setEp);

  /* Sections start collapsed · a 38-section episode is otherwise a very
   * long page. `openIds` is the set of expanded cards; anything just
   * created, inserted or duplicated opens and scrolls into view. */
  const [openIds, setOpenIds] = useState(() => new Set());
  const [insertAfterId, setInsertAfterId] = useState(null);
  const toggleOpen = (sid) => setOpenIds(prev => {
    const next = new Set(prev);
    if (next.has(sid)) next.delete(sid); else next.add(sid);
    return next;
  });
  const revealSection = async (sid) => {
    setOpenIds(prev => new Set(prev).add(sid));
    await reload();
    requestAnimationFrame(() => {
      document.getElementById(`section-${sid}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };
  /* Place `newId` right after `afterId` (or at the end) and renumber ·
   * the server hands out sort_order in steps of ten, this keeps it so. */
  const placeAfter = async (newId, afterId) => {
    const ids = (ep.sections || []).map(s => s.id).filter(x => x !== newId);
    const at = afterId == null ? ids.length : ids.indexOf(afterId) + 1;
    ids.splice(at, 0, newId);
    await api.adminReorderSections(id, ids);
  };

  const addSection = async (type, afterId = null) => {
    const after = afterId != null ? (ep.sections || []).find(s => s.id === afterId) : null;
    const created = await api.adminCreateSection(id, {
      type, emoji: '', title: '', body_markdown: '', data: {},
      ...(after ? { sort_order: (after.sort_order || 0) + 5 } : {}),
    });
    if (after) await placeAfter(created.id, afterId);
    setInsertAfterId(null);
    await revealSection(created.id);
  };

  const move = async (idx, dir) => {
    const sec = ep.sections || [];
    const j = idx + dir;
    if (j < 0 || j >= sec.length) return;
    const ids = sec.map(s => s.id);
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    await api.adminReorderSections(id, ids);
    reload();
  };

  const reorderByDragAndDrop = async (fromId, toId) => {
    if (fromId === toId) return;
    const sec = ep.sections || [];
    const ids = sec.map(s => s.id);
    const fromIdx = ids.indexOf(fromId);
    const toIdx = ids.indexOf(toId);
    if (fromIdx < 0 || toIdx < 0) return;
    ids.splice(fromIdx, 1);
    ids.splice(toIdx, 0, fromId);
    await api.adminReorderSections(id, ids);
    reload();
  };

  const duplicateSection = async (sec) => {
    // server-side copy · media come along (they did not before)
    const copy = await api.adminDuplicateSection(sec.id);
    await placeAfter(copy.id, sec.id);
    await revealSection(copy.id);
  };

  const deleteSection = async (sec) => {
    if (!confirm(t('nl.deleteSectionConfirm'))) return;
    await api.adminDeleteSection(sec.id);
    reload();
  };

  /* Section orchestration · each SectionEditor reports its dirty bit
   * via the imperative handle ref. The sticky save bar at the bottom
   * uses the count + saveAll() to commit every change in one click. */
  const sectionRefs = useRef({});
  const [dirtySectionIds, setDirtySectionIds] = useState(() => new Set());
  // HTML5 drag/drop reorder · the grip on each section card emits
  // dragstart with its id; sibling cards listen for dragover + drop
  // and call reorderByDragAndDrop. dragOverId is just for the visual
  // highlight on the target card.
  const [dragSourceId, setDragSourceId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  /* `noteDirty` is wrapped in useCallback so its reference stays
   * stable across parent renders · without it the child SectionEditor's
   * effect (which has onDirtyChange in its dep list) would re-fire
   * every render and re-trigger this setter, which would then re-build
   * the Set and re-render the parent again. Inside, we bail out when
   * the dirty bit didn't actually flip so React skips the no-op
   * re-render even with referentially-different inputs. */
  const noteDirty = useCallback((sid, isDirty) => {
    setDirtySectionIds(prev => {
      const has = prev.has(sid);
      if (has === !!isDirty) return prev;  // no change, keep the same Set ref
      const next = new Set(prev);
      if (isDirty) next.add(sid); else next.delete(sid);
      return next;
    });
  }, []);
  const headerDirty = useMemo(() => {
    if (!ep || !headerDraft) return false;
    if (coverFile) return true;
    const baseline = {
      number: ep.number, status: ep.status, locale: ep.locale,
      title: ep.title || '', teaser: ep.teaser || '', date_label: ep.date_label || '',
      translations: normalizeTranslations(ep.translations, ['title', 'teaser', 'date_label']),
    };
    return JSON.stringify({ ...baseline }) !== JSON.stringify({
      number: headerDraft.number, status: headerDraft.status,
      locale: headerDraft.locale,
      title: headerDraft.title || '', teaser: headerDraft.teaser || '',
      date_label: headerDraft.date_label || '',
      translations: normalizeTranslations(headerDraft.translations, ['title', 'teaser', 'date_label']),
    });
  }, [ep, headerDraft, coverFile]);

  const dirtyCount = dirtySectionIds.size + (headerDirty ? 1 : 0);

  const [savingAll, setSavingAll] = useState(false);
  const saveAll = async () => {
    setSavingAll(true);
    try {
      const promises = [];
      if (headerDirty) promises.push(saveHeader());
      for (const sid of dirtySectionIds) {
        const ref = sectionRefs.current[sid];
        if (ref?.save) promises.push(ref.save());
      }
      await Promise.all(promises);
      reload();
    } finally { setSavingAll(false); }
  };

  /* Tout traduire · fans out the FR copy across header + every
   * section in parallel. Each section's ref decides whether it has
   * anything worth translating (empty FR → no API call). Result
   * merges into the active locale slot without overwriting other
   * fields, so a partial auto-fill + manual tweak still works. */
  const [translatingAll, setTranslatingAll] = useState(false);
  const [translateProgress, setTranslateProgress] = useState(null);   // { done, total }
  const [translateErrors, setTranslateErrors] = useState([]);
  const translateAll = async () => {
    if (lang === 'fr') return;
    const jobs = [];
    const headerFields = {
      title: headerDraft.title || '',
      teaser: headerDraft.teaser || '',
      date_label: headerDraft.date_label || '',
    };
    if (Object.values(headerFields).some(v => (v || '').trim())) {
      jobs.push({
        label: t('nl.header'),
        run: () => api.adminTranslate('fr', lang, headerFields).then(out => {
          setHeaderDraft(d => ({
            ...d,
            translations: {
              ...(d.translations || {}),
              [lang]: { ...((d.translations || {})[lang] || {}), ...out },
            },
          }));
        }),
      });
    }
    for (const sec of ep.sections || []) {
      const ref = sectionRefs.current[sec.id];
      if (ref?.translateFrom) {
        jobs.push({
          label: `${t(`nl.type.${sec.type}`)} · ${sec.title || t('nl.untitledShort')}`,
          run: () => ref.translateFrom('fr', lang),
        });
      }
    }
    // One block at a time · thirty-nine parallel calls hit the API's rate
    // limit, and one failure used to sink the whole batch without a word.
    setTranslatingAll(true);
    setTranslateErrors([]);
    setTranslateProgress({ done: 0, total: jobs.length });
    const errors = [];
    for (const job of jobs) {
      try {
        await job.run();
      } catch (e) {
        errors.push(`${job.label} : ${e?.response?.data?.detail || e?.message || t('nl.failed')}`);
      }
      setTranslateProgress(p => ({ total: p?.total || jobs.length, done: (p?.done || 0) + 1 }));
    }
    setTranslateErrors(errors);
    setTranslateProgress(null);
    setTranslatingAll(false);
  };

  /* Publish · one action with a confirmation panel: pending edits are
   * saved first, the server records the moment, and #be-team gets a post
   * when asked. The note under the header says what actually happened. */
  const [publishPanel, setPublishPanel] = useState(null);   // 'publish' | 'unpublish' | null
  const [publishing, setPublishing] = useState(false);
  const [publishNote, setPublishNote] = useState('');
  const runPublish = async (mode, announce) => {
    setPublishing(true); setPublishNote('');
    try {
      if (dirtyCount > 0) await saveAll();
      if (mode === 'publish') {
        const res = await api.adminPublishNewsletter(id, announce);
        const slackKey = { posted: 'posted', already: 'already', skipped: 'skipped', failed: 'failed' }[res?.slack] || 'none';
        setPublishNote(`${t('nl.published')} · ${t(`nl.slack.${slackKey}`)}.`);
      } else {
        await api.adminUnpublishNewsletter(id);
        setPublishNote(t('nl.unpublished'));
      }
      setPublishPanel(null);
      await load();
    } catch (e) {
      setPublishNote(e?.response?.data?.detail || e?.message || t('nl.failedDot'));
    } finally { setPublishing(false); }
  };

  /* PDF · the episode in the language of the tab above, for sharing with
   * people who have no hub account. Rendered server-side, so what lands in
   * the file is what the page shows. */
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const downloadPdf = async () => {
    setPdfBusy(true); setPdfError('');
    try {
      if (dirtyCount > 0) await saveAll();   // otherwise the PDF is yesterday's text
      const blob = await api.newsletterPdf(ep.slug, lang);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `MANEVA-${ep.number}-${lang}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setPdfError(e?.response?.data?.detail || e?.message || t('nl.pdf.failed'));
    } finally { setPdfBusy(false); }
  };

  if (ep === undefined || headerDraft === null) {
    return <Shell><LoadingBar /></Shell>;
  }

  return (
    <Shell>
      <Link to="/admin/newsletters"
            className="inline-flex items-center gap-1.5 text-[13px] mb-3"
            style={{ color: LT.green50 }}>
        <ArrowLeft className="h-3.5 w-3.5" /> {t('nl.allEpisodes')}
      </Link>

      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] font-medium font-num"
               style={{ color: LT.green50 }}>
            MANEVA · {t('maneva.episode')} {ep.number}
          </div>
          <h1 className="font-display text-[1.8rem] sm:text-[2.2rem] leading-tight mt-1"
              style={{ color: LT.greenDeep }}>
            {ep.title || t('nl.untitled')}
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <StatusPill status={ep.status} />
          {ep.status === 'published' ? (
            <button onClick={() => setPublishPanel(p => p ? null : 'unpublish')}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12.5px] font-medium"
                    style={{ border: `1px solid ${LT.green30}`, color: LT.greenDeep, background: '#fff' }}>
              <EyeOff className="h-3.5 w-3.5" /> {t('nl.unpublish')}
            </button>
          ) : (
            <button onClick={() => setPublishPanel(p => p ? null : 'publish')}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12.5px] font-medium"
                    style={{ background: LT.greenDeep, color: '#fefefe' }}>
              <Send className="h-3.5 w-3.5" /> {t('nl.publish')}
            </button>
          )}
          <Link to={`/resources/newsletters/${ep.slug}`} target="_blank"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12.5px] font-medium"
                style={{ border: `1px solid ${LT.green30}`, color: LT.greenDeep, background: '#fff' }}>
            <Eye className="h-3.5 w-3.5" /> {ep.status === 'published' ? t('nl.view') : t('nl.previewDraft')}
          </Link>
          <button onClick={downloadPdf} disabled={pdfBusy}
                  title={t('nl.pdf.title', { lang: LANG_LABEL[lang] })}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12.5px] font-medium disabled:opacity-50"
                  style={{ border: `1px solid ${LT.green30}`, color: LT.greenDeep, background: '#fff' }}>
            {pdfBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />}
            {pdfBusy ? t('nl.pdf.busy') : `${t('nl.pdf')} · ${lang.toUpperCase()}`}
          </button>
          <button onClick={deleteEpisode}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12.5px] font-medium"
                  style={{ color: '#b54338' }}>
            <Trash2 className="h-3.5 w-3.5" /> {t('common.delete')}
          </button>
        </div>
      </header>

      {publishPanel && (
        <PublishPanel mode={publishPanel} ep={ep} pending={dirtyCount} busy={publishing}
                      onCancel={() => setPublishPanel(null)}
                      onConfirm={(announce) => runPublish(publishPanel, announce)} />
      )}
      {pdfError && (
        <div className="mb-6 rounded-xl px-4 py-2.5 text-[13px] flex items-center gap-2"
             style={{ background: '#fdecea', color: '#b54338', border: '1px solid #f1c7c2' }}>
          <AlertCircle className="h-3.5 w-3.5 shrink-0" /> {pdfError}
        </div>
      )}
      {publishNote && (
        <div className="mb-6 rounded-xl px-4 py-2.5 text-[13px] flex items-center gap-2"
             style={{ background: '#e5efe7', color: '#3a6f4a' }}>
          <CheckCircle className="h-3.5 w-3.5 shrink-0" /> {publishNote}
        </div>
      )}

      <ReadersPanel newsletterId={id} status={ep.status} />

      <section className="mb-6 rounded-2xl p-5"
               style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}` }}>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div className="text-[11px] uppercase tracking-[0.14em] font-medium"
               style={{ color: LT.green50 }}>
            {t('nl.header')}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {lang !== 'fr' && (
              <button
                type="button"
                onClick={translateAll}
                disabled={translatingAll}
                title={t('nl.translateAll.title', { lang: LANG_LABEL[lang] })}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12.5px] font-medium disabled:opacity-50"
                style={{ background: LT.greenDeep, color: '#fefefe' }}>
                {translatingAll
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <Sparkles className="h-3.5 w-3.5" />}
                {translateProgress
                  ? t('nl.translateAll.progress', { done: translateProgress.done, total: translateProgress.total })
                  : t('nl.translateAll', { lang: LANG_LABEL[lang] })}
              </button>
            )}
            <LangTabs value={lang} onChange={setLang}
                      status={tabStatusFor(headerLocalizedFor(headerDraft), ['title', 'teaser', 'date_label'])} />
          </div>
        </div>
        {translateErrors.length > 0 && (
          <div className="mb-3 rounded-xl px-4 py-2.5 text-[12.5px]"
               style={{ background: '#fdecea', color: '#b54338', border: '1px solid #f1c7c2' }}>
            <div className="font-medium flex items-center gap-1.5">
              <AlertCircle className="h-3.5 w-3.5" />
              {translateErrors.length === 1 ? t('nl.translateAll.failed1') : t('nl.translateAll.failedN', { n: translateErrors.length })}
              · {t('nl.translateAll.retry')}
            </div>
            <ul className="mt-1 list-disc pl-5 space-y-0.5">
              {translateErrors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <Field label={t('nl.field.number')} required className="md:col-span-1">
            <input type="number" value={headerDraft.number} onChange={e => setHeaderDraft({ ...headerDraft, number: parseInt(e.target.value, 10) || 0 })}
                   className="ed-input" />
          </Field>
          <Field label={t('nl.field.address')} hint={t('nl.field.address.hint')} className="md:col-span-2">
            <div className="ed-input font-num text-[12.5px] truncate" title={`/resources/newsletters/${ep.slug}`}
                 style={{ background: 'rgba(43,65,56,0.04)', color: LT.green50 }}>
              …/{ep.slug}
            </div>
          </Field>
          <Field label={`${t('nl.field.dateLabel')} (${LANG_LABEL[lang]})`} hint={t('nl.field.dateLabel.hint')} className="md:col-span-3">
            <input value={headerLocalizedField(headerDraft, lang, 'date_label')}
                   onChange={e => setHeaderDraft(setLocalizedField(headerDraft, lang, 'date_label', e.target.value))}
                   className="ed-input" />
          </Field>
          <Field label={`${t('nl.field.title')} (${LANG_LABEL[lang]})`} required={lang === 'fr'} className="md:col-span-6">
            <input value={headerLocalizedField(headerDraft, lang, 'title')}
                   onChange={e => setHeaderDraft(setLocalizedField(headerDraft, lang, 'title', e.target.value))}
                   className="ed-input" />
          </Field>
          <Field label={`${t('nl.field.teaser')} (${LANG_LABEL[lang]})`}
                 hint={t('nl.field.markdownHint')} className="md:col-span-4">
            <MarkdownTextarea rows={5}
                              value={headerLocalizedField(headerDraft, lang, 'teaser')}
                              onChange={v => setHeaderDraft(setLocalizedField(headerDraft, lang, 'teaser', v))} />
          </Field>
          <Field label={t('nl.field.cover')} hint={t('nl.field.cover.hint')} className="md:col-span-2">
            <div className="flex flex-col gap-2">
              {(coverFile || ep.cover) && (
                <div className="w-full aspect-video rounded-lg overflow-hidden bg-[#1c2a23]">
                  <img src={coverFile ? URL.createObjectURL(coverFile) : ep.cover}
                       alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12px] cursor-pointer self-start"
                     style={{ border: `1px solid ${LT.green30}`, color: LT.greenDeep, background: '#fff' }}>
                <ImagePlus className="h-3.5 w-3.5" />
                {coverFile ? coverFile.name : (ep.cover ? t('photos.admin.replaceCta') : t('photos.admin.uploadCta'))}
                <input type="file" accept="image/*" className="hidden"
                       onChange={e => setCoverFile(e.target.files[0] || null)} />
              </label>
            </div>
          </Field>
        </div>
      </section>

      <section className="pb-24">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <div className="text-[11px] uppercase tracking-[0.14em] font-medium"
               style={{ color: LT.green50 }}>
            {t('nl.sections')} ({ep.sections?.length || 0})
          </div>
          <div className="flex items-center gap-1 text-[12px]">
            <button className="ed-btn-ghost" onClick={() => setOpenIds(new Set((ep.sections || []).map(s => s.id)))}>
              {t('nl.expandAll')}
            </button>
            <button className="ed-btn-ghost" onClick={() => setOpenIds(new Set())}>
              {t('nl.collapseAll')}
            </button>
          </div>
        </div>
        <div className="space-y-3">
          {(ep.sections || []).map((sec, idx) => (
            <React.Fragment key={sec.id}>
            <SectionEditor
              ref={(el) => {
                if (el) sectionRefs.current[sec.id] = el;
                else delete sectionRefs.current[sec.id];
              }}
              section={sec}
              idx={idx}
              total={ep.sections.length}
              onReload={reload}
              onMove={(dir) => move(idx, dir)}
              onDuplicate={duplicateSection}
              onDelete={deleteSection}
              onDirtyChange={noteDirty}
              lang={lang}
              open={openIds.has(sec.id)}
              onToggle={() => toggleOpen(sec.id)}
              onInsertAfter={() => setInsertAfterId(v => (v === sec.id ? null : sec.id))}
              isDragTarget={dragOverId === sec.id && dragSourceId !== sec.id}
              onDragStart={(e) => {
                setDragSourceId(sec.id);
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', String(sec.id));
              }}
              onDragOver={(e) => {
                if (dragSourceId == null || dragSourceId === sec.id) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (dragOverId !== sec.id) setDragOverId(sec.id);
              }}
              onDrop={(e) => {
                e.preventDefault();
                const fromId = dragSourceId;
                setDragSourceId(null);
                setDragOverId(null);
                if (fromId != null && fromId !== sec.id) {
                  reorderByDragAndDrop(fromId, sec.id);
                }
              }}
            />
            {insertAfterId === sec.id && (
              <AddSection forceOpen
                          label={t('nl.insertHere')}
                          onAdd={(type) => addSection(type, sec.id)}
                          onClose={() => setInsertAfterId(null)} />
            )}
            </React.Fragment>
          ))}
        </div>
        <AddSection onAdd={(type) => addSection(type)} />
      </section>

      {/* Sticky save bar · the only "Save" surface on the page now.
          Aggregates header + section drafts and saves them in parallel.
          Hidden when nothing is dirty so the chrome stays calm. */}
      {dirtyCount > 0 && (
        <div className="fixed bottom-0 inset-x-0 z-30"
             style={{ background: `${LT.beigeLight}f5`, borderTop: `1px solid ${LT.green30}`,
                      backdropFilter: 'blur(8px)' }}>
          <div className="max-w-[1480px] mx-auto px-8 py-3 flex items-center gap-3">
            <span className="text-[12.5px]" style={{ color: LT.green50 }}>
              {dirtyCount === 1 ? t('nl.pending1') : t('nl.pendingN', { n: dirtyCount })}
            </span>
            <span className="flex-1" />
            <button onClick={saveAll} disabled={savingAll}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[13px] font-medium disabled:opacity-50"
                    style={{ background: LT.greenDeep, color: '#fefefe' }}>
              {savingAll ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              {t('nl.saveAll')}
            </button>
          </div>
        </div>
      )}
    </Shell>
  );
};

/* ─────────────────────────────────────────────────────────────────
 * One section · always-expanded editor with live preview on the
 * right. Per-section save buttons are gone · the parent collects
 * dirty state via the imperative handle and orchestrates a single
 * "Tout enregistrer" call from the sticky save bar.
 * ──────────────────────────────────────────────────────────────── */
const SectionEditor = forwardRef(function SectionEditor(
  { section, idx, total, onMove, onDelete, onDuplicate, lang, onDirtyChange,
    onDragStart, onDragOver, onDrop, isDragTarget, open, onToggle, onInsertAfter },
  ref,
) {
  const t = useT();
  const [draft, setDraft] = useState(() => ({
    ...section,
    translations: normalizeTranslations(section.translations, ['title', 'body_markdown']),
  }));
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setDraft({
      ...section,
      translations: normalizeTranslations(section.translations, ['title', 'body_markdown']),
    });
  }, [section.id, section.updated_at]);  // eslint-disable-line react-hooks/exhaustive-deps

  const baseline = useMemo(() => ({
    ...section,
    translations: normalizeTranslations(section.translations, ['title', 'body_markdown']),
  }), [section]);
  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(baseline), [draft, baseline]);

  // Tell the parent whenever our dirty bit flips so the sticky save
  // bar's counter stays accurate without polling.
  useEffect(() => { onDirtyChange?.(section.id, dirty); }, [dirty, section.id, onDirtyChange]);

  /* Translate just this block · source locale (FR by default) → target,
   * merged into the section's translations. Shared by the per-block
   * "Traduire" button below and the header's bulk "Tout traduire". */
  const translateSection = async (sourceLang, targetLang) => {
    // Skip sections that have nothing useful to translate (h3 with
    // no title, dividers, …) · they'd just burn tokens.
    const sourceTitle = sourceLang === 'fr'
      ? (draft.title || '')
      : (draft.translations?.[sourceLang]?.title) || '';
    const sourceBody = sourceLang === 'fr'
      ? (draft.body_markdown || '')
      : (draft.translations?.[sourceLang]?.body_markdown) || '';
    const sourceItems = (sourceLang === 'fr'
      ? (draft.data?.items)
      : draft.translations?.[sourceLang]?.items) || draft.data?.items || [];

    const sourcePayload = { title: sourceTitle, body_markdown: sourceBody };
    // For bullet sections, fold the items into the same call as
    // numbered fields · Claude returns the same keys back.
    const isBullets = draft.type === 'bullets';
    if (isBullets) {
      sourceItems.forEach((it, i) => { sourcePayload[`item_${i}`] = it || ''; });
    }
    const hasAnySource = Object.values(sourcePayload).some(v => (v || '').trim() !== '');
    if (!hasAnySource) return;
    const out = await api.adminTranslate(sourceLang, targetLang, sourcePayload);
    setDraft(d => {
      const next = {
        ...d,
        translations: {
          ...(d.translations || {}),
          [targetLang]: {
            ...((d.translations || {})[targetLang] || {}),
            ...(out.title != null ? { title: out.title } : {}),
            ...(out.body_markdown != null ? { body_markdown: out.body_markdown } : {}),
          },
        },
      };
      if (isBullets) {
        const translatedItems = sourceItems.map((_, i) => out[`item_${i}`] ?? sourceItems[i]);
        next.translations[targetLang].items = translatedItems;
      }
      return next;
    });
  };

  // Per-block translate button · FR → the active (non-FR) tab. Busy
  // state is local so each section spins independently.
  const [translating, setTranslating] = useState(false);
  const [translateError, setTranslateError] = useState('');
  const translateToActive = async () => {
    if (lang === 'fr') return;
    setTranslating(true); setTranslateError('');
    try { await translateSection('fr', lang); }
    catch (e) { setTranslateError(e?.response?.data?.detail || e?.message || t('events.admin.translateFailed')); }
    finally { setTranslating(false); }
  };

  useImperativeHandle(ref, () => ({
    isDirty: () => dirty,
    getDraft: () => draft,
    save: async () => {
      if (!dirty) return;
      await api.adminUpdateSection(section.id, {
        type: draft.type, emoji: draft.emoji || '', title: draft.title || '',
        body_markdown: draft.body_markdown || '', data: cleanSectionData(draft.data),
        translations: cleanTranslations(draft.translations),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    },
    translateFrom: translateSection,
  }), [dirty, draft, section.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      id={`section-${section.id}`}
      className="rounded-2xl"
      style={{
        background: LT.beigeLight,
        border: `1px solid ${isDragTarget ? LT.greenDeep : LT.green30}`,
        boxShadow: isDragTarget ? `0 0 0 3px ${LT.green30}` : undefined,
      }}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <header className="flex items-center gap-2 px-4 py-3">
        {/* Real drag handle · grab the grip to reorder. Buttons stay
            as a fallback for keyboard / no-DnD environments. */}
        <span
          draggable
          onDragStart={onDragStart}
          className="cursor-grab active:cursor-grabbing"
          title={t('nl.dragHint')}
          aria-label={t('nl.dragHint')}
        >
          <GripVertical className="h-4 w-4 shrink-0" style={{ color: LT.green50 }} />
        </span>
        {/* The label + title strip is the collapse toggle · the card opens
            on click, the action buttons on the right stay their own thing. */}
        <button type="button" onClick={onToggle}
                className="flex-1 min-w-0 flex items-center gap-2 text-left"
                title={open ? t('nl.collapse') : t('nl.expand')} aria-expanded={open}>
          {open ? <ChevronUp className="h-3.5 w-3.5 shrink-0" style={{ color: LT.green50 }} />
                : <ChevronDown className="h-3.5 w-3.5 shrink-0" style={{ color: LT.green50 }} />}
          <span className="text-[10.5px] uppercase tracking-[0.14em] font-num shrink-0 px-2 py-0.5 rounded-md"
                style={{ background: '#fff', color: LT.green50, border: `1px solid ${LT.green30}` }}>
            {t(`nl.type.${section.type}`)}
          </span>
          <span className="flex-1 min-w-0 truncate text-[13.5px]" style={{ color: LT.greenDeep }}>
            {draft.emoji} {draft.title || <em style={{ color: LT.green50 }}>{t('nl.untitledShort')}</em>}
          </span>
        </button>
        {translateError && (
          <span className="text-[11px] truncate max-w-[220px]" style={{ color: '#b54338' }} title={translateError}>
            {translateError}
          </span>
        )}
        {dirty && (
          <span className="text-[10.5px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-md"
                style={{ background: '#fff3cd', color: '#7c5e00' }}>
            {t('nl.unsaved')}
          </span>
        )}
        {saved && !dirty && (
          <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: '#3a6f4a' }}>
            <CheckCircle className="h-3 w-3" /> ok
          </span>
        )}
        {lang !== 'fr' && (
          <button onClick={translateToActive} disabled={translating}
                  className="ed-btn-ghost inline-flex items-center gap-1 disabled:opacity-40"
                  title={t('nl.translateBlock.title', { lang: LANG_LABEL[lang] })}>
            {translating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            <span className="hidden sm:inline text-[11px]">{t('nl.translate')}</span>
          </button>
        )}
        <button onClick={() => onMove(-1)} disabled={idx === 0}
                className="ed-btn-ghost disabled:opacity-30" title={t('nl.moveUp')}>
          <ChevronUp className="h-3.5 w-3.5" />
        </button>
        <button onClick={() => onMove(1)} disabled={idx === total - 1}
                className="ed-btn-ghost disabled:opacity-30" title={t('nl.moveDown')}>
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
        <button onClick={() => onDuplicate(section)} className="ed-btn-ghost" title={t('nl.duplicate')}>
          <Copy className="h-3.5 w-3.5" />
        </button>
        <button onClick={onInsertAfter} className="ed-btn-ghost" title={t('nl.insertAfter')}>
          <Plus className="h-3.5 w-3.5" />
        </button>
        <button onClick={() => onDelete(section)} className="ed-btn-ghost" title={t('common.delete')}>
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </header>

      {open && (
      <div className="px-4 pb-4 grid grid-cols-1 lg:grid-cols-12 gap-4 border-t"
           style={{ borderColor: LT.green30 }}>
        {/* Edit pane · 7 / 12 cols */}
        <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-6 gap-3">
          <Field label={t('nl.field.type')} className="md:col-span-2">
            <select value={draft.type} onChange={e => setDraft({ ...draft, type: e.target.value })}
                    className="ed-input">
              {SECTION_TYPES.map(st => (
                <option key={st.value} value={st.value}>{t(`nl.type.${st.value}`)}</option>
              ))}
            </select>
          </Field>
          {draft.type !== 'h3' && (
            <Field label={t('nl.field.emoji')} hint={t('nl.optional')} className="md:col-span-1">
              <input value={draft.emoji} onChange={e => setDraft({ ...draft, emoji: e.target.value })}
                     className="ed-input" maxLength={4} />
            </Field>
          )}
          <Field label={`${t('nl.field.title')} (${LANG_LABEL[lang]})`} className="md:col-span-3">
            <input value={sectionLocalizedField(draft, lang, 'title')}
                   onChange={e => setDraft(d => setLocalizedField(d, lang, 'title', e.target.value))}
                   className="ed-input" />
          </Field>

          <TypeSpecificFields draft={draft} setDraft={setDraft} lang={lang} />
        </div>

        {/* Live preview · 5 / 12 cols, sticky as you scroll the section */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-4">
            <div className="text-[10.5px] uppercase tracking-[0.14em] font-medium mb-2"
                 style={{ color: LT.green50 }}>
              {t('nl.livePreview')}
            </div>
            <div className="rounded-xl p-4 bg-white" style={{ border: `1px solid ${LT.green30}` }}>
              <SectionRenderer section={draft} locale={lang} />
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
});

const TypeSpecificFields = ({ draft, setDraft, lang }) => {
  const t = useT();
  const updateData = (patch) => setDraft({ ...draft, data: { ...(draft.data || {}), ...patch } });
  const body = sectionLocalizedField(draft, lang, 'body_markdown');
  const setBody = (v) => setDraft(d => setLocalizedField(d, lang, 'body_markdown', v));

  switch (draft.type) {
    case 'h3':
      return null;  // h3 uses only `title`

    case 'text':
    case 'quote':
      return (
        <>
          <Field label={`${t('nl.field.body')} (${LANG_LABEL[lang]})`}
                 hint={t('nl.field.body.hint')}
                 className="md:col-span-6">
            <MarkdownTextarea rows={12} value={body} onChange={setBody} />
          </Field>
          {draft.type === 'text' && (
            <AttachedImages draft={draft} setDraft={setDraft} updateData={updateData} />
          )}
        </>
      );

    case 'bullets': {
      // Bullet items are now per-locale · stored under
      // translations[lang].items so an editor on the NL tab can
      // rewrite each line in Dutch. FR tab edits the legacy
      // `data.items` for backward compat with rows that predate this.
      const items = lang === 'fr'
        ? (draft.data?.items || [])
        : ((draft.translations?.[lang]?.items) ?? (draft.data?.items || []));
      const setItems = (next) => {
        if (lang === 'fr') {
          setDraft(d => ({ ...d, data: { ...(d.data || {}), items: next } }));
        } else {
          setDraft(d => ({
            ...d,
            translations: {
              ...(d.translations || {}),
              [lang]: { ...((d.translations || {})[lang] || {}), items: next },
            },
          }));
        }
      };
      return (
        <>
          <Field label={`${t('nl.field.intro')} (${LANG_LABEL[lang]})`} hint={t('nl.field.intro.hint')} className="md:col-span-6">
            <MarkdownTextarea rows={3} value={body} onChange={setBody} />
          </Field>
          <Field label={`${t('nl.field.items')} (${LANG_LABEL[lang]})`} hint={t('nl.field.items.hint')} className="md:col-span-6">
            <textarea rows={5}
                      value={items.join('\n')}
                      onChange={e => setItems(e.target.value.split('\n'))}
                      className="ed-textarea" />
          </Field>
          <AttachedImages draft={draft} setDraft={setDraft} updateData={updateData} />
        </>
      );
    }

    case 'clue':
      return (
        <>
          <Field label={t('nl.field.clueLabel')} hint={t('nl.field.clueLabel.hint')} className="md:col-span-2">
            <input value={draft.data?.label || ''} onChange={e => updateData({ label: e.target.value })}
                   className="ed-input" />
          </Field>
          <Field label={`${t('nl.field.clueBody')} (${LANG_LABEL[lang]})`} className="md:col-span-6">
            <MarkdownTextarea rows={5} value={body} onChange={setBody} />
          </Field>
        </>
      );

    case 'fin_table':
      return (
        <>
          <Field label={`${t('nl.field.intro')} (${LANG_LABEL[lang]})`} hint={t('nl.optional')} className="md:col-span-6">
            <MarkdownTextarea rows={3} value={body} onChange={setBody} />
          </Field>
          <Field label={t('nl.field.headers')} hint={t('nl.field.headers.hint')} className="md:col-span-3">
            <textarea rows={5}
                      value={(draft.data?.headers || []).join('\n')}
                      onChange={e => updateData({ headers: e.target.value.split('\n') })}
                      className="ed-textarea" />
          </Field>
          <Field label={t('nl.field.rows')} hint={t('nl.field.rows.hint')} className="md:col-span-3">
            <textarea rows={5}
                      value={(draft.data?.rows || []).map(r => r.join(' | ')).join('\n')}
                      onChange={e => updateData({
                        rows: e.target.value.split('\n').map(line => line.split('|').map(c => c.trim())),
                      })}
                      className="ed-textarea" />
          </Field>
          <Field label={t('nl.field.footer')} hint={t('nl.field.footer.hint')} className="md:col-span-3">
            <textarea rows={3}
                      value={(draft.data?.footer || []).join('\n')}
                      onChange={e => updateData({ footer: e.target.value.split('\n') })}
                      className="ed-textarea" />
          </Field>
        </>
      );

    case 'gallery':
      return (
        <GalleryEditor draft={draft} setDraft={setDraft} updateData={updateData} lang={lang} />
      );

    default:
      return null;
  }
};

const GalleryEditor = ({ draft, setDraft, updateData, lang }) => {
  const t = useT();
  return (
    <>
      <Field label={`${t('nl.field.galleryIntro')} (${LANG_LABEL[lang]})`} hint={t('nl.optional')} className="md:col-span-6">
        <MarkdownTextarea rows={3}
                          value={sectionLocalizedField(draft, lang, 'body_markdown')}
                          onChange={v => setDraft(d => setLocalizedField(d, lang, 'body_markdown', v))} />
      </Field>
      <AttachedImages draft={draft} setDraft={setDraft} updateData={updateData} label={t('nl.media')} />
    </>
  );
};

/* Image-strip editor that any section type can host. The renderer
 * (mergeGalleryImages) already lifts both `section.images` and the
 * legacy `data.image_urls` into the gallery panel under the body,
 * so text and bullets sections can carry images too · this component
 * exposes the upload + delete UX consistently across types. */
const AttachedImages = ({ draft, setDraft, updateData, label }) => {
  const t = useT();
  const [uploading, setUploading] = useState(false);
  const seedUrls = draft.data?.image_urls || [];
  const uploaded = draft.images || [];

  const handleUpload = async (file) => {
    if (!file || !draft.id) return;  // need a server id to attach
    setUploading(true);
    try {
      const created = await api.adminUploadSectionImage(draft.id, file);
      setDraft({ ...draft, images: [...uploaded, created] });
    } finally { setUploading(false); }
  };
  const removeSeed = (url) => updateData({ image_urls: seedUrls.filter(u => u !== url) });
  // A LampTwist Drive "Copy file link" (or any https image/video address) ·
  // lives in data.image_urls, so it is saved with the text on "Save all".
  const [link, setLink] = useState('');
  const linkOk = /^https:\/\/\S+$/i.test(link.trim());
  const addLink = () => {
    const url = link.trim();
    if (!linkOk) return;
    if (!seedUrls.includes(url)) updateData({ image_urls: [...seedUrls, url] });
    setLink('');
  };
  const removeUploaded = async (imgId) => {
    await api.adminDeleteSectionImage(imgId);
    setDraft({ ...draft, images: uploaded.filter(i => i.id !== imgId) });
  };

  const empty = uploaded.length === 0 && seedUrls.length === 0;
  return (
    <div className="md:col-span-6">
      <div className="text-[11px] uppercase tracking-[0.12em] font-num font-medium mb-2"
           style={{ color: LT.green50 }}>
        {label || t('nl.media.attached')}
      </div>
      <div className="text-[11.5px] mb-2" style={{ color: LT.green50 }}>
        {t('nl.media.hint')}
      </div>
      {empty && (
        <div className="text-[12px] mb-2" style={{ color: LT.green50 }}>
          {t('nl.media.empty')}
        </div>
      )}
      {!empty && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {uploaded.map(img => (
            <ImageTile key={img.id} src={img.image} kind={img.kind || 'image'}
                       onRemove={() => removeUploaded(img.id)} />
          ))}
          {seedUrls.map(url => (
            <ImageTile key={url} src={url} kind={mediaKindFromUrl(url)}
                       onRemove={() => removeSeed(url)}
                       badge={/^https?:/i.test(url) ? t('nl.media.linkBadge') : 'seed'} />
          ))}
        </div>
      )}
      <label className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12.5px] cursor-pointer self-start"
             style={{ border: `1px solid ${LT.green30}`, color: LT.greenDeep, background: '#fff' }}>
        {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
        {uploading ? t('nl.media.uploading') : t('nl.media.add')}
        <input type="file"
               accept="image/*,video/mp4,video/webm,video/quicktime"
               className="hidden"
               disabled={!draft.id}
               onChange={e => handleUpload(e.target.files[0])} />
      </label>
      <form className="mt-3 flex flex-wrap items-center gap-2"
            onSubmit={e => { e.preventDefault(); addLink(); }}>
        <input type="url" value={link} onChange={e => setLink(e.target.value)}
               placeholder={t('nl.media.linkPlaceholder')}
               aria-label={t('nl.media.linkPlaceholder')}
               className="min-w-0 flex-1 px-3 py-2 rounded-xl text-[12.5px]"
               style={{ border: `1px solid ${LT.green30}`, background: '#fff' }} />
        <button type="submit" disabled={!linkOk}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12.5px] disabled:opacity-50"
                style={{ border: `1px solid ${LT.green30}`, color: LT.greenDeep, background: '#fff' }}>
          <Link2 className="h-3.5 w-3.5" />
          {t('nl.media.addLink')}
        </button>
      </form>
      <div className="text-[11.5px] mt-1.5" style={{ color: LT.green50 }}>
        {t('nl.media.linkHint')}
      </div>
    </div>
  );
};

const ImageTile = ({ src, kind = 'image', onRemove, badge }) => {
  const t = useT();
  return (
  <div className="relative aspect-square rounded-xl overflow-hidden"
       style={{ background: '#1c2a23', border: `1px solid ${LT.green30}` }}>
    {kind === 'video' ? (
      // `preload="metadata"` pulls a poster frame without the full file.
      // muted keeps autoplay policies quiet if a browser previews on hover.
      <video src={src} muted preload="metadata"
             className="w-full h-full object-cover" />
    ) : (
      <img src={src} alt="" className="w-full h-full object-cover" />
    )}
    {kind === 'video' && (
      <span className="absolute bottom-1.5 left-1.5 text-[9.5px] font-num uppercase tracking-[0.1em] px-1.5 py-0.5 rounded"
            style={{ background: 'rgba(0,0,0,0.6)', color: '#fefefe' }}>
        {t('nl.media.video')}
      </span>
    )}
    {badge && (
      <span className="absolute top-1.5 left-1.5 text-[9.5px] font-num uppercase tracking-[0.1em] px-1.5 py-0.5 rounded"
            style={{ background: 'rgba(255,255,255,0.85)', color: LT.green50 }}>
        {badge}
      </span>
    )}
    <button onClick={onRemove}
            className="absolute top-1.5 right-1.5 h-6 w-6 rounded-md flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.55)', color: '#fefefe' }}
            title={t('nl.media.remove')}>
      <Trash2 className="h-3 w-3" />
    </button>
  </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
 * Add-section picker · grid of type buttons for fast insertion.
 * ──────────────────────────────────────────────────────────────── */
const AddSection = ({ onAdd, forceOpen = false, onClose, label }) => {
  const t = useT();
  const [open, setOpen] = useState(!!forceOpen);
  const close = () => { setOpen(false); onClose?.(); };
  return (
    <div className={forceOpen ? 'mt-1' : 'mt-6'}>
      {!open ? (
        <button onClick={() => setOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-medium transition"
                style={{ background: LT.greenDeep, color: '#fefefe' }}>
          <Plus className="h-4 w-4" /> {t('nl.addSection')}
        </button>
      ) : (
        <div className="rounded-2xl p-4"
             style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}` }}>
          <div className="text-[11px] uppercase tracking-[0.14em] font-medium mb-3"
               style={{ color: LT.green50 }}>
            {label || t('nl.pickType')}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
            {SECTION_TYPES.map(st => (
              <button key={st.value}
                      onClick={() => { onAdd(st.value); close(); }}
                      className="flex flex-col items-start gap-1 p-3 rounded-xl text-left transition hover:-translate-y-px"
                      style={{ background: '#fff', border: `1px solid ${LT.green30}` }}>
                <span className="text-[15px]">{st.glyph}</span>
                <span className="text-[12.5px] font-medium" style={{ color: LT.greenDeep }}>
                  {t(`nl.type.${st.value}`)}
                </span>
                <span className="text-[10.5px]" style={{ color: LT.green50 }}>
                  {t(`nl.type.${st.value}.hint`)}
                </span>
              </button>
            ))}
          </div>
          <button onClick={close} className="mt-3 ed-btn-ghost">
            {t('common.cancel')}
          </button>
        </div>
      )}
    </div>
  );
};

/* Labels and hints live in translations as nl.type.<value> / .hint */
const SECTION_TYPES = [
  { value: 'text',      glyph: '📝' },
  { value: 'h3',        glyph: 'H₃' },
  { value: 'bullets',   glyph: '•'  },
  { value: 'gallery',   glyph: '🖼️' },
  { value: 'fin_table', glyph: '📊' },
  { value: 'clue',      glyph: '💡' },
  { value: 'quote',     glyph: '“'  },
];

/* Blank lines are kept in the draft while typing · a controlled textarea
 * that filtered them on every keystroke ate the Enter key, so a second
 * bullet or table row could only be pasted in. They are dropped here,
 * on save, and the renderer ignores them anyway. */
const compact = (arr) => (Array.isArray(arr) ? arr.filter(v => (v || '').trim() !== '') : arr);
function cleanSectionData(data) {
  const d = { ...(data || {}) };
  if (Array.isArray(d.items)) d.items = compact(d.items);
  if (Array.isArray(d.headers)) {
    d.headers = [...d.headers];
    while (d.headers.length && !(d.headers[d.headers.length - 1] || '').trim()) d.headers.pop();
  }
  if (Array.isArray(d.rows)) d.rows = d.rows.filter(r => Array.isArray(r) && r.some(c => (c || '').trim() !== ''));
  if (Array.isArray(d.footer)) d.footer = compact(d.footer);
  return d;
}
function cleanTranslations(tr) {
  const out = {};
  for (const [lang, slot] of Object.entries(tr || {})) {
    out[lang] = Array.isArray(slot?.items) ? { ...slot, items: compact(slot.items) } : slot;
  }
  return out;
}
